import { create } from "zustand";
import Communicator from "../Helper/Communicator";
import {
  alignCaptionSpeech,
  type CaptionAlignment,
  CaptionReadingCursor,
  captionTokens,
  captionWordsShown,
  completeCaptionSentence,
  countCaptionWords,
  startCaptionAlignment,
} from "../Helper/KaiVoiceCaption";
import { resetHoldMusic, startHoldMusic, stopHoldMusic } from "../Helper/KaiVoiceHoldMusic";
import { getCallAudioContext, playEndTone, startConnectTone, stopConnectTone } from "../Helper/KaiVoiceTones";
import {
  KaiVoiceConnection,
  SPEAKING_DECAY_MS,
  type LiveEvent,
} from "../Helper/KaiVoiceConnection";
import {
  CreateVoiceCallResponse,
  HttpCallsService,
} from "../Services/http.clients/http.voice.client";
import { SILENT_LEVELS } from "../Views/KaiVoiceCall/kaiVoiceOrb";
import type { VoiceLevels } from "../Views/KaiVoiceCall/kaiVoiceOrb";
import { useConfigStore } from "./Config";
import { useRouterStore } from "./Router";
import { useSessionStore } from "./Session";
import { useTicketStore } from "./Ticket";
import {
  drainDeferredCallActions,
  setVoiceCallBusy,
  setVoiceHandoffHandler,
  setVoiceOverlayVisible,
} from "./VoiceCallActivity";

export type KaiVoiceCallState =
  | "idle"
  | "requestingMic"
  | "connecting"
  | "active"
  | "ending"
  | "ended"
  | "error";

export type KaiVoiceMode = "listening" | "thinking" | "speaking";

export type KaiVoiceErrorType =
  | "micDenied"
  | "policyBlocked"
  | "connectFailed"
  | "dropped"
  | "busy"
  | "rateLimited"
  | "unavailable";

interface KaiVoiceStore {
  callState: KaiVoiceCallState;
  mode: KaiVoiceMode;
  errorType?: KaiVoiceErrorType;
  muted: boolean;
  startedAtMs?: number;
  maxDurationSec: number;
  callId?: string;
  /**
   * Server call id known from the create POST onwards — set BEFORE the
   * WebRTC handshake completes so a cancel-while-connecting can hand the
   * server's concurrency slot back (callId is only set once active).
   */
  pendingCallId?: string;
  conversationShareToken?: string;
  /** Kai's current answer (live caption). */
  captionText: string;
  captionKey: number;
  /** Counts customer turns — the caption remounts (and fades in) once per answer, not per sentence. */
  captionTurn: number;
  /** Words of `captionText` Kai has spoken — the caption shows the current sentence up to there. */
  captionSpokenWords: number;
  /** Where each pushed sentence of `captionText` ends (the server pushes one sentence at a time). */
  captionChunkEnds: number[];
  /** The server's caption id: a new one is a new answer, never a continuation. */
  captionSegment?: number;
  /** Kai Resolve is working on the request: the line is on hold and hold music plays. */
  onHold: boolean;
  /** Storybook/mock harness: freeze the store — never touch WebRTC/network. */
  previewMode: boolean;

  startCall: (args?: { conversationShareToken?: string }) => Promise<void>;
  toggleMute: () => void;
  endCall: () => Promise<void>;
  /** Server-initiated hang-up (voice.call.ended push) — idempotent. */
  handleServerEnded: (actionData?: { callId?: string; reason?: string }) => void;
  /** Kai's answer text for the current turn (voice.caption push). */
  handleCaption: (actionData?: { callId?: string; text?: string; segment?: number; hold?: boolean }) => void;
  /** The server put the call on hold for Kai Resolve, or took it off (voice.hold push). */
  handleHold: (actionData?: { callId?: string; active?: boolean }) => void;
  /**
   * The server pushed a widget navigation (prefilled bug/feature form) as
   * the outcome of a spoken turn: let Kai finish the handover sentence, then
   * hang up straight into it — no "call ended" screen in between.
   */
  handoffToWidget: (action: () => void) => void;
  dismiss: () => void;
  /** 0..1 mic/agent level for the waveform + particles (0 in preview). */
  getAudioLevel: () => number;
  /** 0..1 per side — Kai and the customer — for the voice orb (silent in preview). */
  getAudioLevels: () => VoiceLevels;
  /** Harness-only: seed an arbitrary call state without any session. */
  seedForPreview: (state: Partial<KaiVoiceStore>) => void;
}

/**
 * One call attempt, from the create POST to teardown. Every async hop in
 * startCall re-checks that the attempt is still the current one: an
 * endCall()/dismiss() during "connecting" bumps the generation, so a late
 * handshake tears its connection down instead of resurrecting a zombie call
 * behind an idle overlay.
 */
interface CallAttempt {
  generation: number;
  callId?: string;
  /** The /end POST has been fired for this attempt (by whichever path got there first). */
  endReported: boolean;
}

// The data channel must be usable within this after the SDP answer, or the
// handshake counts as failed.
const READY_TIMEOUT_MS = 8000;
// The ready report (the server greets then) waits for the remote audio to be
// playing (bounded) plus a short settle so the connect tone has finished and
// the jitter buffer has packets in it — otherwise the greeting's first
// syllables stutter now and then.
const PLAYBACK_TIMEOUT_MS = 2500;
const GREETING_SETTLE_MS = 350;

let callGeneration = 0;
let currentAttempt: CallAttempt | null = null;
let liveConnection: KaiVoiceConnection | null = null;
let widgetStopTimer: ReturnType<typeof setTimeout> | null = null;
let speakingDecayTimer: ReturnType<typeof setTimeout> | null = null;
/** GPT-Live delegated the request to Kai and no answer text has arrived yet. */
let awaitingAnswer = false;
// Caption pacing: GPT-Live's transcript since the caption began is followed
// word by word through the caption (`alignCaptionSpeech`), from the last
// finished sentence (`captionCheckpoint`) on. A caption that arrives while
// GPT-Live is still saying its "one moment" starts at its own first words.
let spokenCaptionTranscript = "";
let captionCheckpoint: CaptionAlignment = startCaptionAlignment(false);
let captionAlignment: CaptionAlignment = captionCheckpoint;
let captionRevealTimer: ReturnType<typeof setTimeout> | null = null;
let captionSettleTimer: ReturnType<typeof setTimeout> | null = null;
// A caption whose speech never shows up (no transcript events) is revealed anyway,
// and so are its later sentences (`captionShownAnyway`).
const CAPTION_REVEAL_FALLBACK_MS = 5000;
let captionShownAnyway = false;
// No transcript for this long means Kai stopped talking, even where its
// voice level cannot be read. Shorter gaps happen inside a sentence: a long
// number is one fragment of 1.5 s.
const CAPTION_SETTLE_MS = 3000;
// Words appear at the pace Kai's voice reads them (`CaptionReadingCursor`),
// up to where the transcript reached (`captionReached`).
let readingCursor = new CaptionReadingCursor(Date.now());
let captionReached = 0;
// GPT-Live's transcript still flowing when a caption arrives: its filler may not
// be over (its own gaps inside a filler reach a second).
const TRANSCRIPT_FLOWING_MS = 1200;
let lastAgentTranscriptAt = 0;
// The customer talked over Kai this recently: Kai stopped, it did not finish its phrase.
const CUSTOMER_BARGE_IN_MS = 1000;
// Kai's voice (and transcript) silent this long: it finished. What the transcript
// reached was said and is shown, one sentence per step so each gets its moment.
const FINISHED_SILENCE_MS = 1500;
const FINISHED_STEP_MS = 700;
let lastFinishedStepAt = 0;
let lastCustomerSpeechAt = 0;
// The caption id of the answer a new request replaced: its late pushes are dropped.
let retiredCaptionSegment: number | undefined;
// A pause of Kai's voice this long finishes the phrase it ended (a reworded end).
const AGENT_PAUSE_MS = 300;
const AGENT_LEVEL_POLL_MS = 20;
const PAUSE_COMPLETES_WORDS = 2;
let agentLevelTimer: ReturnType<typeof setInterval> | null = null;
let pauseHandled = false;

// Hold music starts once Kai has finished the hand-over line (the hold push
// usually lands before GPT-Live starts saying it), or after this long.
let holdMusicAwaitsSpeech = false;
let holdPushedAt = 0;
let agentSpeechStartedAt = 0;
// The hand-over line starts after the push; the previous answer's tail started long before it.
const HOLD_LINE_START_SLACK_MS = 500;
let holdMusicGateTimer: ReturnType<typeof setTimeout> | null = null;
let holdSafetyTimer: ReturnType<typeof setTimeout> | null = null;
const HOLD_MUSIC_GATE_MS = 2500;
// The server ends a hold after 90 s; a lost "hold off" push must not leave the music on.
const HOLD_SAFETY_MS = 100_000;

// Mid-call handoff: the server pushed a widget navigation (prefilled bug /
// feature form) while Kai is still speaking the handover line. The action is
// parked here and runs on the way to idle — WHATEVER ends the call (our own
// hang-up once the speech stops, the customer pressing End, the session
// closing, the server's fallback end) — so it can never get lost.
let pendingHandoffAction: (() => void) | null = null;
let agentSpeaking = false;
let handoffTimers: Array<ReturnType<typeof setTimeout>> = [];
let handoffFinish: (() => void) | null = null;
// The handover line may not have started when the push lands.
const HANDOFF_SPEECH_START_GRACE_MS = 2500;
// Transcript fragments pause between sentences; only a longer pause counts as "done talking".
const HANDOFF_SPEECH_END_DEBOUNCE_MS = 900;
const HANDOFF_MAX_WAIT_MS = 9000;

const clearHandoffTimers = () => {
  handoffFinish = null;
  handoffTimers.forEach((timer) => clearTimeout(timer));
  handoffTimers = [];
};

/** Hold music plays while the call is on hold and Kai is not talking over it. */
const syncHoldMusic = () => {
  const { callState, onHold, previewMode } = useKaiVoiceStore.getState();
  if (!previewMode && callState === "active" && onHold && !agentSpeaking && !holdMusicAwaitsSpeech) {
    startHoldMusic();
  } else {
    stopHoldMusic();
  }
};

const clearRevealFallback = () => {
  if (captionRevealTimer) {
    clearTimeout(captionRevealTimer);
    captionRevealTimer = null;
  }
};

const clearCaptionRevealTimer = () => {
  clearRevealFallback();
  if (captionSettleTimer) {
    clearTimeout(captionSettleTimer);
    captionSettleTimer = null;
  }
};

const clearHoldTimers = () => {
  holdMusicAwaitsSpeech = false;
  if (holdMusicGateTimer) {
    clearTimeout(holdMusicGateTimer);
    holdMusicGateTimer = null;
  }
  if (holdSafetyTimer) {
    clearTimeout(holdSafetyTimer);
    holdSafetyTimer = null;
  }
};

const showCaptionWords = (words: number) => {
  // Never backwards: a word once shown stays.
  if (words > useKaiVoiceStore.getState().captionSpokenWords) useKaiVoiceStore.setState({ captionSpokenWords: words });
};

/** A new caption starts hidden and follows the voice; `CAPTION_REVEAL_FALLBACK_MS` without speech shows it anyway. */
const startCaptionPacing = (text: string) => {
  spokenCaptionTranscript = "";
  captionCheckpoint = startCaptionAlignment(
    Date.now() - lastAgentTranscriptAt < TRANSCRIPT_FLOWING_MS && captionTokens(text).length > 0,
  );
  captionAlignment = captionCheckpoint;
  captionReached = 0;
  readingCursor.setCaption(text, useKaiVoiceStore.getState().captionChunkEnds);
  readingCursor.reset(Date.now());
  clearCaptionRevealTimer();
  captionShownAnyway = false;
  captionRevealTimer = setTimeout(() => {
    captionRevealTimer = null;
    if (useKaiVoiceStore.getState().captionSpokenWords === 0) revealCaptionAnyway();
  }, CAPTION_REVEAL_FALLBACK_MS);
};

/** The caption without its speech: one sentence per step, as if Kai had said it. */
const revealCaptionAnyway = () => {
  captionRevealTimer = null;
  captionShownAnyway = true;
  const total = countCaptionWords(useKaiVoiceStore.getState().captionText);
  if (!total) return;
  const words = readingCursor.finish(total);
  showCaptionWords(words);
  if (words < total) captionRevealTimer = setTimeout(revealCaptionAnyway, FINISHED_STEP_MS);
};

const reachCaption = (alignment: CaptionAlignment) => {
  captionAlignment = alignment;
  const { captionText, captionChunkEnds } = useKaiVoiceStore.getState();
  captionReached = captionWordsShown(captionText, captionChunkEnds, alignment);
};

const alignCaptionToTranscript = () => {
  reachCaption(alignCaptionSpeech(useKaiVoiceStore.getState().captionText, spokenCaptionTranscript, captionCheckpoint));
};

/** Kai finished talking: the next sentence of what the transcript reached is shown. */
const finishNextSentence = (now: number): number => {
  lastFinishedStepAt = now;
  completeSpokenSentence();
  return readingCursor.finish(captionReached);
};

/** A long transcript silence (also where no meter hears the voice): the rest is shown sentence by sentence. */
const settleCaption = () => {
  captionSettleTimer = null;
  if (!useKaiVoiceStore.getState().captionText) return;
  const now = Date.now();
  if (now - lastFinishedStepAt >= FINISHED_STEP_MS) showCaptionWords(finishNextSentence(now));
  if (useKaiVoiceStore.getState().captionSpokenWords < captionReached) captionSettleTimer = setTimeout(settleCaption, FINISHED_STEP_MS);
};

/** Kai stopped talking: the sentence it was in was said (see completeCaptionSentence). */
const completeSpokenSentence = (maxMissing?: number) => {
  const { captionText, captionChunkEnds } = useKaiVoiceStore.getState();
  if (!captionText) return;
  const completed = completeCaptionSentence(captionText, captionChunkEnds, spokenCaptionTranscript, captionAlignment, maxMissing);
  if (completed === captionAlignment) return;
  captionCheckpoint = completed;
  // The words after it may already be the next sentence's.
  alignCaptionToTranscript();
};

const stopAgentPauseWatch = () => {
  if (agentLevelTimer) {
    clearInterval(agentLevelTimer);
    agentLevelTimer = null;
  }
};

/** Moves the caption along with Kai's voice; a pause finishes the phrase it ended. */
const watchAgentVoice = (connection: KaiVoiceConnection) => {
  stopAgentPauseWatch();
  readingCursor = new CaptionReadingCursor(Date.now());
  const { captionText, captionChunkEnds } = useKaiVoiceStore.getState();
  if (captionText) readingCursor.setCaption(captionText, captionChunkEnds);
  agentLevelTimer = setInterval(() => {
    if (!useKaiVoiceStore.getState().captionText) return;
    const now = Date.now();
    let words = readingCursor.tick(now, captionReached, connection.getAgentLevel());
    const silent = readingCursor.silentFor(now);
    if (silent !== null && silent >= AGENT_PAUSE_MS) {
      // A pause while the transcript still streams is a comma or a sentence break the
      // transcript already ran past; the customer talking means Kai was cut off.
      const quiet = now - lastAgentTranscriptAt >= AGENT_PAUSE_MS && now - lastCustomerSpeechAt >= CUSTOMER_BARGE_IN_MS;
      if (!pauseHandled && quiet) {
        pauseHandled = true;
        completeSpokenSentence(PAUSE_COMPLETES_WORDS);
        words = readingCursor.finishPhrase(captionReached);
      }
      const finished =
        silent >= FINISHED_SILENCE_MS &&
        now - lastAgentTranscriptAt >= FINISHED_SILENCE_MS &&
        now - lastCustomerSpeechAt >= CUSTOMER_BARGE_IN_MS;
      if (finished && now - lastFinishedStepAt >= FINISHED_STEP_MS) words = finishNextSentence(now);
    } else {
      pauseHandled = false;
    }
    showCaptionWords(words);
  }, AGENT_LEVEL_POLL_MS);
};

const onAgentSpeakingChange = (speaking: boolean) => {
  const wasSpeaking = agentSpeaking;
  agentSpeaking = speaking;
  if (speaking && !wasSpeaking) agentSpeechStartedAt = Date.now();
  if (!speaking && wasSpeaking && holdMusicAwaitsSpeech && agentSpeechStartedAt >= holdPushedAt - HOLD_LINE_START_SLACK_MS) {
    holdMusicAwaitsSpeech = false;
    if (holdMusicGateTimer) {
      clearTimeout(holdMusicGateTimer);
      holdMusicGateTimer = null;
    }
  }
  syncHoldMusic();
  if (!handoffFinish) return;
  if (speaking) {
    // Next sentence started — cancel a running end-of-speech debounce (the
    // grace / cap timers are the first two entries and stay).
    handoffTimers.splice(2).forEach((timer) => clearTimeout(timer));
    return;
  }
  if (wasSpeaking) {
    const finish = handoffFinish;
    handoffTimers.push(
      setTimeout(() => {
        if (!agentSpeaking) finish();
      }, HANDOFF_SPEECH_END_DEBOUNCE_MS),
    );
  }
};

const isCurrentAttempt = (attempt: CallAttempt): boolean =>
  attempt.generation === callGeneration;

/**
 * Fire the server-side /end exactly once per attempt (idempotent server-side
 * anyway). The server tells a hang-up from a call that never connected.
 */
const reportEnd = (attempt: CallAttempt | null): void => {
  if (!attempt || attempt.endReported || !attempt.callId) return;
  attempt.endReported = true;
  HttpCallsService.getInstance().endCall(attempt.callId).catch(() => {});
};

const clearWidgetStopTimer = () => {
  if (widgetStopTimer) {
    clearTimeout(widgetStopTimer);
    widgetStopTimer = null;
  }
};

const clearSpeakingDecay = () => {
  if (speakingDecayTimer) {
    clearTimeout(speakingDecayTimer);
    speakingDecayTimer = null;
  }
};

/** Hang up the media session (asks GPT-Live to close, then tears the peer connection down). */
const releaseConnection = () => {
  const connection = liveConnection;
  liveConnection = null;
  clearWidgetStopTimer();
  clearSpeakingDecay();
  clearCaptionRevealTimer();
  stopAgentPauseWatch();
  clearHoldTimers();
  stopHoldMusic();
  awaitingAnswer = false;
  connection?.close();
};

/**
 * Detect a host page that never delegated the microphone (Permissions-Policy).
 * A plain user denial also rejects getUserMedia — this distinguishes "your
 * site blocks it" from "you clicked block" for the error copy.
 */
const isMicBlockedByPolicy = (): boolean => {
  try {
    const policy =
      (document as any).permissionsPolicy ?? (document as any).featurePolicy;
    if (policy?.allowsFeature) {
      return policy.allowsFeature("microphone") === false;
    }
  } catch (_) {}
  return false;
};

/**
 * Map a create (POST /calls) rejection to an error screen. The XHR client
 * rejects with `{ status, statusText, data }` (see http.client.ts) — status 0
 * is a network error/timeout and falls through to the generic copy.
 */
const errorTypeForBootstrapFailure = (err: any): KaiVoiceErrorType => {
  const status = typeof err?.status === "number" ? err.status : 0;
  switch (status) {
    case 409:
      return "busy";
    case 429:
      return "rateLimited";
    case 400:
    case 402:
    case 403:
      return "unavailable";
    default:
      return "connectFailed";
  }
};

const finishCall = () => {
  Communicator.kaiVoiceEnded();
  useTicketStore.getState().clearBotWrittingIndicator();
  useTicketStore.getState().loadConversations();
};

export const useKaiVoiceStore = create<KaiVoiceStore>()((set, get) => {
  /**
   * GPT-Live's data-channel events drive the overlay: who is talking, and
   * when Kai's finished answer moves up to make room for the next one. The
   * caption text itself comes from the server (`handleCaption`) so it only
   * ever shows Kai's answer, never GPT-Live's own acknowledgements.
   */
  const handleLiveEvent = (event: LiveEvent) => {
    switch (event.type) {
      case "session.input_transcript.delta":
        // The customer is talking. The caption stays: trailing ASR fragments
        // (or a "mhm") arrive while Kai is already answering and must not
        // wipe the answer — a new request clears it (delegation below).
        lastCustomerSpeechAt = Date.now();
        if (get().mode !== "thinking") set({ mode: "listening" });
        break;
      case "session.delegation.created": {
        // Kai got a new request: the finished answer leaves the stage.
        awaitingAnswer = true;
        spokenCaptionTranscript = "";
        clearCaptionRevealTimer();
        captionReached = 0;
        const state = get();
        // The server finishes the replaced answer's turn for a moment: its pushes must not bring it back.
        if (state.captionSegment !== undefined) retiredCaptionSegment = state.captionSegment;
        // The customer is heard again, so any hold is over (its push may be lost).
        if (state.onHold) clearHoldTimers();
        set({
          mode: "thinking",
          captionSpokenWords: 0,
          captionChunkEnds: [],
          captionSegment: undefined,
          onHold: false,
          ...(state.captionText ? { captionText: "", captionTurn: state.captionTurn + 1 } : {}),
        });
        syncHoldMusic();
        break;
      }
      case "session.output_transcript.delta": {
        // Only Kai's answers are captioned: before the first caption of a
        // request arrives, what GPT-Live says is its own "one moment".
        lastAgentTranscriptAt = Date.now();
        if (get().captionText) {
          if (captionSettleTimer) clearTimeout(captionSettleTimer);
          spokenCaptionTranscript += event.delta ?? "";
          readingCursor.transcriptArrived(Date.now());
          alignCaptionToTranscript();
          // The caption's own words: the voice paces them. While only the filler is heard,
          // the fallback stays (a reworded short answer never matches the opening).
          if (!captionAlignment.awaitingOpening) {
            clearRevealFallback();
            captionShownAnyway = false;
          }
          captionSettleTimer = setTimeout(settleCaption, CAPTION_SETTLE_MS);
        }
        set({ mode: "speaking" });
        onAgentSpeakingChange(true);
        clearSpeakingDecay();
        speakingDecayTimer = setTimeout(() => {
          speakingDecayTimer = null;
          onAgentSpeakingChange(false);
          if (get().mode !== "speaking") return;
          set({ mode: get().onHold ? "thinking" : awaitingAnswer && !get().captionText ? "thinking" : "listening" });
        }, SPEAKING_DECAY_MS);
        break;
      }
      default:
        break;
    }
  };

  return {
    callState: "idle",
    mode: "listening",
    errorType: undefined,
    muted: false,
    startedAtMs: undefined,
    maxDurationSec: 300,
    callId: undefined,
    pendingCallId: undefined,
    conversationShareToken: undefined,
    captionText: "",
    captionKey: 0,
    captionTurn: 0,
    captionSpokenWords: 0,
    captionChunkEnds: [],
    captionSegment: undefined,
    onHold: false,
    previewMode: false,

    startCall: async (args) => {
      const state = get();
      if (state.previewMode) return;
      if (["requestingMic", "connecting", "active", "ending"].includes(state.callState)) {
        return;
      }

      const attempt: CallAttempt = { generation: ++callGeneration, endReported: false };
      currentAttempt = attempt;
      awaitingAnswer = false;
      clearHandoffTimers();
      pendingHandoffAction = null;
      agentSpeaking = false;
      spokenCaptionTranscript = "";
      retiredCaptionSegment = undefined;
      lastCustomerSpeechAt = 0;
      clearHoldTimers();
      resetHoldMusic();
      // Still inside the click: Safari only lets an AudioContext play that was
      // created or resumed in a user gesture (tones and hold music share it).
      getCallAudioContext();

      set({
        callState: "requestingMic",
        errorType: undefined,
        muted: false,
        captionText: "",
        captionSpokenWords: 0,
        captionChunkEnds: [],
        captionSegment: undefined,
        onHold: false,
        mode: "listening",
        callId: undefined,
        pendingCallId: undefined,
      });

      // Acquire the permission BEFORE flipping into the call layout — same
      // gotcha AudioRecorderUpload documents: flipping first shows a broken
      // shell behind the browser's permission prompt. The probe stream is
      // released immediately; the connection opens its own.
      try {
        const probeStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        probeStream.getTracks().forEach((track) => track.stop());
      } catch (err: any) {
        if (!isCurrentAttempt(attempt)) return;
        const policyBlocked = isMicBlockedByPolicy();
        set({
          callState: "error",
          errorType: policyBlocked ? "policyBlocked" : "micDenied",
        });
        return;
      }
      if (!isCurrentAttempt(attempt)) return;

      set({ callState: "connecting" });

      let created: CreateVoiceCallResponse | null = null;

      // GPT-Live closed the session (the customer's hang-up echoed back, the
      // server ended the call, the provider gave up).
      const onSessionClosed = () => {
        const current = get().callState;
        if (current !== "active" && current !== "connecting") return;
        releaseConnection();
        reportEnd(attempt);
        if (current === "connecting") {
          set({ callState: "error", errorType: "connectFailed" });
        } else {
          set({ callState: "ended" });
        }
        finishCall();
        // A parked form handoff must not wait behind the ended screen.
        if (pendingHandoffAction) get().dismiss();
      };

      try {
        const connection = await KaiVoiceConnection.open({
          exchangeSdp: async (offer) => {
            const configStore = useConfigStore.getState();
            const sessionStore = useSessionStore.getState();
            const response = await HttpCallsService.getInstance().startCall({
              sdp: offer,
              conversationShareToken: args?.conversationShareToken,
              botId: (configStore.config as any)?.botId,
              lang: (sessionStore.session as any)?.lang,
            });
            created = response;
            attempt.callId = response.callId;
            if (!isCurrentAttempt(attempt) || get().callState !== "connecting") {
              // Cancelled while the request was in flight — hand the slot back.
              reportEnd(attempt);
              throw new Error("cancelled");
            }
            set({ pendingCallId: response.callId });
            return response.sdp;
          },
          handlers: {
            onEvent: (event) => {
              if (!isCurrentAttempt(attempt)) return;
              if (event.type === "session.closed") {
                onSessionClosed();
                return;
              }
              handleLiveEvent(event);
            },
            onConnectionLost: () => {
              if (!isCurrentAttempt(attempt)) return;
              const current = get().callState;
              if (current !== "active" && current !== "connecting") return;
              releaseConnection();
              reportEnd(attempt);
              if (current === "connecting") {
                set({ callState: "error", errorType: "connectFailed" });
              } else {
                console.error("[gleap-voice] connection lost");
                set({ callState: "error", errorType: "dropped" });
              }
              finishCall();
            },
          },
        });

        if (!isCurrentAttempt(attempt) || get().callState !== "connecting" || !created) {
          // endCall()/dismiss() landed while the handshake was pending: the
          // overlay is already gone, so this connection must not outlive it.
          connection.close();
          reportEnd(attempt);
          return;
        }
        liveConnection = connection;
        watchAgentVoice(connection);

        const ready = await connection.waitForReady(READY_TIMEOUT_MS);
        if (!isCurrentAttempt(attempt) || get().callState !== "connecting") {
          releaseConnection();
          reportEnd(attempt);
          return;
        }
        if (!ready) {
          throw new Error("GPT-Live data channel did not open");
        }
        const response: CreateVoiceCallResponse = created;

        set({
          callState: "active",
          startedAtMs: Date.now(),
          callId: response.callId,
          conversationShareToken: response.conversationShareToken,
          maxDurationSec: response.maxDurationSec,
        });
        Communicator.kaiVoiceStarted();
        // The server greets (and captions it) once the widget's audio plays and the connect tone is over.
        connection.waitForPlayback(PLAYBACK_TIMEOUT_MS).then(() => {
          setTimeout(() => {
            if (!isCurrentAttempt(attempt) || liveConnection !== connection) return;
            // Lost for good, the server ends the call after its ready timeout.
            HttpCallsService.getInstance().markReady(response.callId).catch(() => {});
          }, GREETING_SETTLE_MS);
        });

        // Belt and braces on top of the server's hard cap.
        clearWidgetStopTimer();
        widgetStopTimer = setTimeout(
          () => {
            get().endCall();
          },
          (response.maxDurationSec + 5) * 1000,
        );

        // A brand-new conversation: point the conversation view at it so the
        // thread behind the overlay (and after the call) is the right one.
        if (!args?.conversationShareToken && response.conversationShareToken) {
          const router = useRouterStore.getState();
          router.setPage("conversation", { shareToken: response.conversationShareToken });
        }
      } catch (err: any) {
        if (!isCurrentAttempt(attempt)) {
          // Already cancelled — whoever cancelled reported the end.
          reportEnd(attempt);
          return;
        }
        releaseConnection();
        if (err?.message !== "cancelled") {
          console.error("[gleap-voice] call could not be started:", err?.message ?? err);
        }
        // Outside the connection: the failure path hands the server's
        // concurrency slot back — without it a failed handshake locks this
        // session out of voice until the lease expires.
        reportEnd(attempt);
        set({
          callState: "error",
          errorType: created ? "connectFailed" : errorTypeForBootstrapFailure(err),
        });
      }
    },

    toggleMute: () => {
      const muted = !get().muted;
      try {
        liveConnection?.setMuted(muted);
      } catch (_) {}
      set({ muted });
    },

    endCall: async () => {
      const { callState, previewMode } = get();
      if (previewMode) return;
      if (callState !== "active" && callState !== "connecting") {
        get().dismiss();
        return;
      }

      // Invalidate the attempt FIRST: a handshake still in flight must see
      // the bump when it resolves and tear itself down.
      clearHandoffTimers();
      const attempt = currentAttempt;
      callGeneration++;
      set({ callState: "ending" });
      releaseConnection();
      // During "connecting" only the create id is known — that's enough for
      // the server to free the slot.
      reportEnd(attempt);
      finishCall();
      if (pendingHandoffAction) {
        // The form is waiting — no "call ended" screen in between.
        get().dismiss();
        return;
      }
      set({ callState: "ended" });
    },

    handleServerEnded: (actionData) => {
      const { callState, callId, pendingCallId } = get();
      if (callState !== "active" && callState !== "connecting") return;
      const knownId = callId ?? pendingCallId;
      // Another call's end: the one this attempt superseded ends while this one still connects (its id unknown yet).
      if (actionData?.callId && actionData.callId !== knownId) return;
      get().endCall();
    },

    handleCaption: (actionData) => {
      const { callState, callId, pendingCallId, captionText, captionKey, captionSegment, captionChunkEnds, onHold } = get();
      // Only a call that runs shows captions: Kai greets once the widget reported ready.
      if (callState !== "active") return;
      const knownId = callId ?? pendingCallId;
      if (actionData?.callId && knownId && actionData.callId !== knownId) return;
      // A lost `voice.hold` push must not leave the music on (or off).
      if (typeof actionData?.hold === "boolean" && actionData.hold !== onHold) {
        get().handleHold({ callId: actionData.callId, active: actionData.hold });
      }
      const text = typeof actionData?.text === "string" ? actionData.text : "";
      const segment = typeof actionData?.segment === "number" ? actionData.segment : undefined;
      if (!text) return;
      // Server caption ids only grow: the answer a new request replaced is over.
      if (segment !== undefined && retiredCaptionSegment !== undefined && segment <= retiredCaptionSegment) return;
      if (text === captionText) {
        if (segment !== undefined) set({ captionSegment: segment });
        return;
      }
      awaitingAnswer = false;
      // The answer so far, one more sentence per push. Servers without caption
      // ids: a push that does not extend the caption starts a new one.
      const continues = segment !== undefined ? segment === captionSegment : !!captionText && text.startsWith(captionText);
      // An older push of the same answer, out of order: the caption already has it.
      if (continues && !text.startsWith(captionText)) return;
      if (continues) {
        set({ captionText: text, captionKey: captionKey + 1, captionChunkEnds: [...captionChunkEnds, text.length] });
        readingCursor.setCaption(text, get().captionChunkEnds);
        // GPT-Live may already be saying the new sentence.
        alignCaptionToTranscript();
        if (captionShownAnyway && !captionRevealTimer) captionRevealTimer = setTimeout(revealCaptionAnyway, FINISHED_STEP_MS);
        return;
      }
      set({
        captionText: text,
        captionKey: captionKey + 1,
        captionSpokenWords: 0,
        captionChunkEnds: [text.length],
        captionSegment: segment,
        captionTurn: get().captionTurn + 1,
      });
      startCaptionPacing(text);
    },

    handleHold: (actionData) => {
      const { callState, callId, pendingCallId, onHold } = get();
      if (callState !== "active") return;
      const knownId = callId ?? pendingCallId;
      if (actionData?.callId && knownId && actionData.callId !== knownId) return;
      const active = actionData?.active === true;
      if (active === onHold) return;
      clearHoldTimers();
      if (active) {
        holdPushedAt = Date.now();
        holdMusicAwaitsSpeech = true;
        holdMusicGateTimer = setTimeout(() => {
          holdMusicGateTimer = null;
          holdMusicAwaitsSpeech = false;
          syncHoldMusic();
        }, HOLD_MUSIC_GATE_MS);
        holdSafetyTimer = setTimeout(() => {
          holdSafetyTimer = null;
          useKaiVoiceStore.setState({ onHold: false });
          syncHoldMusic();
        }, HOLD_SAFETY_MS);
      }
      set({
        onHold: active,
        ...(active ? { mode: "thinking" as const } : get().mode === "thinking" && !awaitingAnswer ? { mode: "listening" as const } : {}),
      });
      syncHoldMusic();
    },

    handoffToWidget: (action) => {
      const { callState, previewMode } = get();
      if (previewMode) return;
      clearHandoffTimers();
      pendingHandoffAction = action;
      if (callState !== "active") {
        // Ended/error screen (or the call is already gone): go straight there.
        get().dismiss();
        return;
      }

      const finish = () => {
        clearHandoffTimers();
        if (get().callState !== "active") {
          get().dismiss();
          return;
        }
        const attempt = currentAttempt;
        callGeneration++;
        releaseConnection();
        reportEnd(attempt);
        finishCall();
        get().dismiss();
      };

      handoffFinish = finish;
      // Order matters: onAgentSpeakingChange keeps the first two timers.
      handoffTimers.push(setTimeout(finish, HANDOFF_MAX_WAIT_MS));
      handoffTimers.push(
        setTimeout(() => {
          // The line may still be on its way to the speaker: give the audio a
          // moment to start; if it never does, don't keep the customer waiting.
          if (!agentSpeaking) finish();
        }, HANDOFF_SPEECH_START_GRACE_MS),
      );
    },

    dismiss: () => {
      clearHandoffTimers();
      agentSpeaking = false;
      callGeneration++;
      releaseConnection();
      set({
        callState: "idle",
        mode: "listening",
        errorType: undefined,
        muted: false,
        startedAtMs: undefined,
        callId: undefined,
        pendingCallId: undefined,
        captionText: "",
        captionSpokenWords: 0,
        captionChunkEnds: [],
        captionSegment: undefined,
        onHold: false,
      });
      // Overlay is gone — run the navigation the server handed over mid-call.
      const action = pendingHandoffAction;
      pendingHandoffAction = null;
      if (action) {
        try {
          action();
        } catch (err) {
          console.error("[gleap-voice] handoff action failed:", err);
        }
      }
    },

    getAudioLevel: () => {
      try {
        return liveConnection?.getLevel() ?? 0;
      } catch (_) {
        return 0;
      }
    },

    getAudioLevels: () => {
      try {
        return liveConnection?.getLevels() ?? SILENT_LEVELS;
      } catch (_) {
        return SILENT_LEVELS;
      }
    },

    seedForPreview: (state) => {
      set({ previewMode: true, ...state });
    },
  };
});

// Mirror the call state into the cycle-free module flags that Ticket.ts reads
// (mute sounds mid-call; defer navigation pushes while the overlay is up).
// Busy is deliberately NOT `!== "idle"`: the post-call ended/error screens
// must not stay muted. The overlay flag IS `!== "idle"` — it tracks what the
// App-level call surface actually covers.
setVoiceHandoffHandler((action) => useKaiVoiceStore.getState().handoffToWidget(action));

const BUSY_CALL_STATES: KaiVoiceCallState[] = ["requestingMic", "connecting", "active", "ending"];
useKaiVoiceStore.subscribe((state, prev) => {
  if (prev.callState === state.callState) return;
  if (!state.previewMode) {
    // Call-progress tones ("Marimba"): a quiet pulse while connecting, a
    // rising tick when the session is up, a low tap on hang-up (never on an
    // error — the error screen already explains what happened).
    if (state.callState === "connecting") {
      startConnectTone();
    } else if (prev.callState === "connecting") {
      stopConnectTone(state.callState === "active");
    }
    if (prev.callState === "active" && ["ending", "ended", "idle"].includes(state.callState)) {
      playEndTone();
    }
  }
  setVoiceCallBusy(BUSY_CALL_STATES.includes(state.callState));
  syncHoldMusic();
  const overlayVisible = state.callState !== "idle";
  setVoiceOverlayVisible(overlayVisible);
  if (!overlayVisible) {
    // Overlay just unmounted — run navigation the server pushed mid-call
    // (e.g. the prefilled bug form after a voice action), and refresh the
    // conversation list so the call's thread is at the top of Messages.
    drainDeferredCallActions();
    useTicketStore.getState().loadConversations();
  }
});

// A reload / navigation mid-call would otherwise leave the server waiting for
// the lease to expire (and the next "Speak to Kai" hitting 409). pagehide is
// the last reliable hook; XHR is torn down with the document, so the /end goes
// out via fetch keepalive.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    const { callState, callId, pendingCallId, previewMode } = useKaiVoiceStore.getState();
    if (previewMode) return;
    if (callState !== "active" && callState !== "connecting") return;
    const id = callId ?? pendingCallId ?? currentAttempt?.callId;
    callGeneration++;
    // Timers and hold music too: a page restored from the back/forward cache must be quiet.
    try {
      releaseConnection();
    } catch (_) {}
    if (id && !(currentAttempt?.endReported)) {
      if (currentAttempt) currentAttempt.endReported = true;
      HttpCallsService.getInstance().endCallKeepalive(id);
    }
  });
}
